import React, { Dispatch, SetStateAction } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import {
  btn,
  btnBox,
  greenBtn,
  menuItems,
  pageHeaderBox,
  showBtn,
  titleBox,
} from 'app/components/PageHeader/PageHeader.sx';
import EditIcon from 'app/components/Icons/EditIcon';
import BackIcon from 'app/components/Icons/BackIcon';
import PlusIcon from 'app/components/Icons/PlusIcon';
import CoinsSwapIcon from 'app/components/Icons/CoinsSwapIcon';
import BankIcon from 'app/components/Icons/BankIcon';
import ManatIcon from 'app/components/Icons/ManatIcon';
import GlobusIcon from 'app/components/Icons/GlobusIcon';
import SwitchIcon from 'app/components/Icons/SwitchIcon';
import CoinsStackedIconSelect from 'app/components/Icons/CoinsStackedIconSelect';
import WalletIconSelect from 'app/components/Icons/WalletIconSelect';
import SaleIcon from 'app/components/Icons/SaleIcon';
import usePageHeader from 'app/components/PageHeader/usePageHeader';
import BinIcon from 'app/components/Icons/BinIcon';
import SaveIcon from 'app/components/Icons/SaveIcon';
import BookmarkIcon from 'app/components/Icons/BookmarkIcon';
import DownloadIcon from 'app/components/Icons/DownloadIcon';
import FileUploadIcon from 'app/components/Icons/FileUploadIcon';
import DownloadAdocIcon from 'app/components/Icons/DownloadAdocIcon';
import SearchInput from 'app/components/SearchInput';
import UsersXIcon from 'app/components/Icons/UsersXIcon';

interface PageProps {
  pageName: string;
  subtitle?: string;
  back?: boolean;
  showBtnBox?: boolean;
  template?: boolean;
  editTemplate?: boolean;
  createTemplate?: boolean;
  saveChanges?: boolean;
  deleteTemplate?: boolean;
  finishedFile?: boolean;
  sampleFileShow?: boolean;
  saveChangesModal?: Dispatch<SetStateAction<boolean>>;
  deleteModal?: Dispatch<SetStateAction<boolean>>;
  searchBar?: boolean;
  handleFileUpload?: () => void;
  handleDownloadFile?: () => void;
  employeesExit?: boolean;
}

const options = [
  {
    value: 'Öz hesablarım arasında',
    label: 'Öz hesablarım arasında',
    icon: <CoinsSwapIcon />,
    templateType: 'payment_acc2acc',
  },
  { value: 'Bankdaxili', label: 'Bankdaxili', icon: <BankIcon />, templateType: 'payment_interbank' },
  { value: 'Ölkədaxili', label: 'Ölkədaxili', icon: <ManatIcon />, templateType: 'payment_local_transfer' },
  { value: 'Ölkəxarici', label: 'Xarici valyutada', icon: <GlobusIcon />, templateType: 'payment_foreign_transfer' },
  {
    value: 'Konvertasiya',
    label: 'Konvertasiya',
    icon: <SwitchIcon sx={{ width: '20px', height: '20px' }} />,
    templateType: 'payment_conversion',
  },
  { value: 'Ədv ödənişi', label: 'Ədv ödənişi', icon: <CoinsStackedIconSelect />, templateType: 'payment_edv' },
  { value: 'Büdcə ödənişi', label: 'Büdcə ödənişi', icon: <WalletIconSelect />, templateType: 'payment_budget' },
];

const PageHeader = (props: PageProps) => {
  const {
    pageName,
    subtitle,
    back,
    showBtnBox,
    template,
    editTemplate,
    saveChanges,
    deleteTemplate,
    createTemplate,
    finishedFile,
    sampleFileShow,
    saveChangesModal,
    deleteModal,
    searchBar,
    handleFileUpload,
    employeesExit,
    handleDownloadFile,
  } = props;
  const {
    open,
    anchorRef,
    navigate,
    lang,
    handleToggle,
    handleTemplates,
    handleListKeyDown,
    handleClose,
    handleBack,
    handleEmployeesExit,
  } = usePageHeader();

  return (
    <Box sx={pageHeaderBox}>
      <Box sx={titleBox}>
        {back && <BackIcon sx={{ '&:hover': { cursor: 'pointer' } }} onClick={handleBack} />}
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: '600' }}>{pageName}</Typography>
          {subtitle && <Typography sx={{ fontSize: '17px', color: 'rgba(20, 25, 31, 0.60)' }}>{subtitle}</Typography>}
        </Box>
      </Box>
      {showBtnBox && (
        <Box sx={btnBox}>
          {finishedFile && (
            <Button sx={greenBtn} onClick={handleFileUpload}>
              <FileUploadIcon color={'secondary'} />
              <Typography sx={{ color: '#ffffff', fontWeight: '500', lineHeight: 1 }}>Hazır faylı yüklə</Typography>
            </Button>
          )}
          {employeesExit && (
            <Button onClick={handleEmployeesExit} variant={'outlined'}>
              <UsersXIcon />
              <Typography sx={{ fontWeight: '600', lineHeight: 1 }}>Siyahıdan çıxardılmış işçilər</Typography>
            </Button>
          )}
          {sampleFileShow && (
            <Button variant={'outlined'} onClick={handleDownloadFile}>
              <DownloadAdocIcon />
              <Typography sx={{ fontWeight: '600', lineHeight: 1 }}>Nümunə fayla bax</Typography>
            </Button>
          )}
          {template && (
            <Button sx={greenBtn} onClick={handleTemplates}>
              <BookmarkIcon />
              <Typography sx={{ color: '#ffffff', lineHeight: 1 }}>Şablonlar</Typography>
            </Button>
          )}
          {editTemplate && (
            <Button sx={greenBtn}>
              <EditIcon color={'warning'} />
              Redaktə et
            </Button>
          )}
          {createTemplate && (
            <>
              <Button
                sx={greenBtn}
                ref={anchorRef}
                id="composition-button"
                aria-controls={open ? 'composition-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
              >
                <PlusIcon />
                Şablon yarat
              </Button>
              <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="top-start"
                transition
                sx={{
                  zIndex: 1000,
                }}
                disablePortal
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: 'right top',
                    }}
                  >
                    <Paper sx={{ boxShadow: '0px 0px 32px 0px rgba(0, 0, 0, 0.06)' }}>
                      <ClickAwayListener onClickAway={handleClose}>
                        <MenuList
                          autoFocusItem={open}
                          id="composition-menu"
                          aria-labelledby="composition-button"
                          onKeyDown={handleListKeyDown}
                        >
                          {options.map((item) => (
                            <MenuItem
                              key={item.value}
                              onClick={(e) => {
                                handleClose(e);
                                navigate(`${lang}/templates-page/new-template?type=${item.templateType}`, {
                                  state: { subtitle: item.value },
                                });
                              }}
                              sx={menuItems}
                            >
                              {item.icon}
                              {item.label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </>
          )}
          {saveChanges && (
            <Button sx={greenBtn} onClick={() => saveChangesModal(true)}>
              <SaveIcon />
              Dəyişiklikləri yadda saxla
            </Button>
          )}
          {deleteTemplate && (
            <Button color={'secondary'} sx={{ gap: '12px' }} onClick={() => deleteModal(true)}>
              <BinIcon />
              Sil
            </Button>
          )}
          {/* {searchBar && <SearchInput />} */}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
